import { Check } from "lucide-react";
import { Reveal } from "@/components/charts/reveal";
import { niceScale } from "@/components/charts/chart-utils";

export type RankRow = { id: string; label: string; value: number; display: string; color?: string; tip?: string[] };

/**
 * Horizontal bars on a zero-based scale with round ticks (styles in app/v2-catalog.css, `vc-rb-*`).
 * Rows arrive in rank order. When `best` is given, the rows holding it get the data-green gradient
 * and a check; every other bar stays neutral. The value is always printed, so colour never carries
 * meaning alone, and hover/focus reveals the exact figure and its source.
 */
export function RankBars({ rows, label, best = null, bestLabel, formatTick, compact = false }: {
  rows: RankRow[]; label: string; best?: number | null; bestLabel: string; formatTick: (value: number) => string; compact?: boolean;
}) {
  const { top, ticks, step } = niceScale(Math.max(0, ...rows.map(row => row.value)));
  const gridSize = `${(step / top) * 100}% 100%`;
  // Long labels ("$0.015") collide on narrow cards: keep every other tick; compact keeps only the ends.
  const crowded = ticks.map(formatTick).join("").length > 24;
  const showTick = (i: number) => i === 0 || i === ticks.length - 1 || (!compact && (!crowded || (i % 2 === 0 && ticks.length - 1 - i >= 2)));
  const rank = (value: number) => rows.findIndex(row => row.value === value) + 1;
  return <Reveal className={compact ? "vc-rb vc-rb-compact" : "vc-rb"} role="group" aria-label={label}>
    <div className="vc-rb-scale" aria-hidden="true">
      <span /><span />
      <span className="vc-rb-ticks">{ticks.map((tick, i) => !showTick(i) ? null : <span key={tick} style={{ left: `${(tick / top) * 100}%` }} data-edge={i === 0 ? "start" : i === ticks.length - 1 ? "end" : undefined}>{formatTick(tick)}</span>)}</span>
      <span />
    </div>
    <ol className="vc-rb-list">
      {rows.map((row, i) => {
        const win = best != null && row.value === best;
        return <li key={row.id} className="vc-rb-row" data-win={win || undefined} tabIndex={row.tip ? 0 : undefined}>
          <span className="vc-rb-rank" aria-hidden="true">{rank(row.value)}</span>
          <span className="vc-rb-name" title={row.label}>{row.color && <i className="vc-dot" style={{ background: row.color }} aria-hidden="true" />}<span>{row.label}</span></span>
          <span className="vc-rb-track" style={{ backgroundSize: gridSize }} aria-hidden="true"><i className="vc-rb-fill vc-grow" style={{ width: `${Math.max(0, row.value) / top * 100}%`, animationDelay: `${Math.min(i, 12) * 45}ms` }} /></span>
          <span className="vc-rb-value">{row.display}{win && <><Check aria-hidden="true" /><span className="ui-sr-only"> ({bestLabel})</span></>}</span>
          {row.tip && <span className="vc-tip" aria-hidden="true">{row.tip.map((line, j) => j === 0 ? <b key={j}>{line}</b> : <span key={j}>{line}</span>)}</span>}
        </li>;
      })}
    </ol>
  </Reveal>;
}
